import { useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import type { DashboardContext, VoiceStudio as Studio } from '../api/types';
import { Card, Eyebrow, H1, LinkButton, Mono } from '../components/primitives';
import { Failure, LoadingPanel } from '../components/states';
import { border, c, radius } from '../theme/tokens';

/** Curated Pro set: ten distinct British voices, intentionally small enough to choose confidently. */
const PRO_VOICE_IDS = ['cartesia-Willa', '11labs-Willa', 'cartesia-Adam', '11labs-Dorothy', 'retell-Willa', '11labs-Anthony', 'cartesia-Anthony', '11labs-Amy', 'openai-Fable', '11labs-Maren'];

/**
 * Voice Studio is intentionally a control plane, not a remote control for live
 * phones. The UI says that repeatedly because the distinction is the product.
 * Browser recording/preview arrives in the next slice, once the server-minted
 * staging web-call token endpoint exists; it must not talk to a prod agent.
 */
export function VoiceStudio({ ctx }: { ctx: DashboardContext }) {
  const [studio, setStudio] = useState<Studio | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [voice, setVoice] = useState('');
  const [voices, setVoices] = useState<{ voice_id: string; voice_name: string | null; provider: string | null; gender: string | null; accent: string | null; preview_audio_url: string | null }[]>([]);
  const [note, setNote] = useState('');

  const load = () => {
    setError(null);
    api.studio(ctx.branch.id).then(setStudio).catch(e => setError(e instanceof ApiError ? e.message : 'Unable to load Voice Studio.'));
  };
  useEffect(load, [ctx.branch.id]);
  useEffect(() => { api.studioVoices(ctx.branch.id).then(result => setVoices(result.voices)).catch(() => setVoices([])); }, [ctx.branch.id]);

  const propose = async () => {
    if (!voice.trim()) return setError('Name the voice you want to trial first. This creates a draft only.');
    setBusy(true); setError(null);
    try {
      await api.proposeVoice({ branchId: ctx.branch.id, kind: 'voice', voiceId: voice, rationale: note.trim() || undefined });
      setVoice(''); setNote(''); load();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Unable to save draft.'); }
    finally { setBusy(false); }
  };

  const lock = async () => {
    setBusy(true); setError(null);
    try { await api.lockStudio(ctx.branch.id); load(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Unable to lock changes.'); }
    finally { setBusy(false); }
  };
  const approve = async () => {
    if (!studio?.changeSet) return;
    setBusy(true); setError(null);
    try { await api.approveStudio(ctx.branch.id, studio.changeSet.id); load(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Unable to approve changes.'); }
    finally { setBusy(false); }
  };

  if (!studio && !error) return <Content><LoadingPanel label="OPENING VOICE STUDIO" /></Content>;
  if (error && !studio) return <Content><Failure message={error} onRetry={load} /></Content>;
  if (!studio) return null;

  const locked = studio.changeSet?.status !== 'open' && studio.changeSet != null;
  const curatedVoices = PRO_VOICE_IDS.map(id => voices.find(v => v.voice_id === id)).filter((v): v is NonNullable<typeof v> => Boolean(v));
  const selectedVoice = curatedVoices.find(v => v.voice_id === voice) ?? null;
  return <Content>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 24, flexWrap: 'wrap' }}>
      <div><Eyebrow>VOICE STUDIO · PRIVATE DRAFTS</Eyebrow><H1>Make the assistant sound like you.</H1></div>
      <Mono size={10.5} tracking={0.1} color={c.success}>LIVE PHONE UNCHANGED</Mono>
    </div>

    <Card padding={26} gap={12}>
      <Mono size={10} tracking={0.13} color={c.indigo}>CHANGE CONTROL</Mono>
      <div style={{ fontSize: 17, color: c.ink, lineHeight: 1.45 }}>{studio.policy.description}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 13.5, color: c.ink2 }}>
        <span>Digest <b>{studio.policy.digestAt}</b></span><span>Lock <b>{studio.policy.lockAt}</b></span><span>Approved release window <b>{studio.policy.publishAt}</b></span>
      </div>
    </Card>

    {error ? <Failure message={error} onRetry={load} /> : null}
    {!studio.branch.hasRetellAgent ? <Failure label="STUDIO NOT CONNECTED" message="This branch has no Retell agent mapping. Voice Studio cannot create a safe draft until an administrator connects one." /> : null}

    <Card padding={26} gap={16}>
      <div style={{ fontSize: 19, color: c.ink }}>Try a voice</div>
      <div style={{ fontSize: 14, color: c.ink3, lineHeight: 1.55 }}>Ten British voices, selected for estate-agency calls. Choose one to queue for the next approved 02:00 release. It cannot change a live call.</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10 }}>
        {curatedVoices.map(v => {
          const selected = voice === v.voice_id;
          return <button key={v.voice_id} className="vp-reset" disabled={!studio.permissions.canPropose || locked} onClick={() => setVoice(v.voice_id)} style={{ textAlign: 'left', padding: '14px', border: `2px solid ${selected ? c.indigo : c.line}`, borderRadius: radius.button, background: selected ? 'rgba(9,64,116,0.05)' : c.surface2, opacity: !studio.permissions.canPropose || locked ? 0.55 : 1 }}>
            <div style={{ fontSize: 16, color: c.ink }}>{v.voice_name ?? v.voice_id}</div>
            <Mono size={10} tracking={0.08} color={c.ink3}>{v.provider?.toUpperCase() ?? 'VOICE'} · BRITISH</Mono>
          </button>;
        })}
      </div>
      {!curatedVoices.length && voices.length ? <Failure label="VOICE CATALOGUE CHANGED" message="The curated British voice set needs refreshing before a selection can be made." /> : null}
      {selectedVoice?.preview_audio_url ? <audio controls preload="none" src={selectedVoice.preview_audio_url} /> : null}
      <textarea value={note} disabled={!studio.permissions.canPropose || locked} onChange={e => setNote(e.target.value)} placeholder="Why this fits the business (optional)" className="vp-outline" style={{ minHeight: 72, padding: 12, borderRadius: radius.button, border: border.default, font: 'inherit', maxWidth: 520 }} />
      <button className="vp-reset" disabled={busy || !studio.permissions.canPropose || locked} onClick={propose} style={primary(disabled(busy || !studio.permissions.canPropose || locked))}>Add to today’s review bundle</button>
    </Card>

    <Card padding={26} gap={14}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' }}><div><div style={{ fontSize: 19, color: c.ink }}>Today’s change bundle</div><Mono size={10} tracking={0.1}>{studio.changeSet ? studio.changeSet.status.toUpperCase() : 'NO CHANGES YET'}</Mono></div>{studio.changeSet?.status === 'open' && studio.permissions.canPropose ? <LinkButton onClick={lock}>Lock and prepare the owner digest →</LinkButton> : null}{studio.changeSet?.status === 'locked' && studio.permissions.canApprove ? <LinkButton onClick={approve}>Approve overnight release →</LinkButton> : null}</div>
      {studio.requests.length === 0 ? <div style={{ color: c.ink3 }}>Nothing proposed today.</div> : studio.requests.map(r => <div key={r.id} style={{ padding: '13px 0', borderBottom: border.default, display: 'flex', justifyContent: 'space-between', gap: 12 }}><span style={{ color: c.ink }}>{r.kind.toUpperCase()} · {String(r.after_value.voiceLabel ?? JSON.stringify(r.after_value))}</span><Mono size={10}>{r.status.toUpperCase()}</Mono></div>)}
      {studio.digest ? <Mono size={10} tracking={0.08} color={c.ink3}>OWNER DIGEST: {studio.digest.status.toUpperCase()} {studio.digest.recipient_email ? `· ${studio.digest.recipient_email}` : '· RECIPIENT NOT YET CONFIGURED'}</Mono> : null}
    </Card>
  </Content>;
}

function Content({ children }: { children: React.ReactNode }) { return <div style={{ padding: '44px 40px 64px', maxWidth: 1080, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 24 }}>{children}</div>; }
function disabled(value: boolean) { return value; }
function primary(isDisabled: boolean) { return { alignSelf: 'flex-start' as const, padding: '10px 16px', borderRadius: radius.button, background: isDisabled ? c.lineSoft : c.indigo, color: isDisabled ? c.ink3 : c.surface2, cursor: isDisabled ? 'not-allowed' : 'pointer', fontSize: 14 }; }
